import { useEffect, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';

import { getThreadSummary } from '../../services/mailIntelApi';
import DraftPanel from './DraftPanel';
import {
  Chip,
  DetailShell,
  EmptyNote,
  SectionLabel,
  StatItem,
  T,
  fmtDate,
  fmtDateTime,
  humanise,
} from './shared';

export const LABEL_TONE = {
  'Decision needed': { fg: T.decision, bg: T.decisionBg },
  Resolved: { fg: T.ok, bg: T.okBg },
  'Update only': { fg: T.muted, bg: 'rgba(161,161,170,0.10)' },
};
export const labelTone = (label) =>
  LABEL_TONE[label] ||
    // "Decision pending with <ROLE>"
    { fg: T.info, bg: T.infoBg };

// PRD bands: Critical / High / Normal / Low.
export const BAND_TONE = {
  critical: { fg: T.danger, bg: T.dangerBg },
  high: { fg: T.warn, bg: T.warnBg },
  normal: { fg: T.info, bg: T.infoBg },
  low: { fg: T.muted, bg: 'rgba(161,161,170,0.10)' },
};

const ROLE_LABEL = {
  pm: 'PM',
  ba: 'BA',
  tl: 'Tech Lead',
  consultant: 'Consultant',
  associate: 'Associate',
};
export const roleLabel = (role) => ROLE_LABEL[role] || role || '—';

const SENTIMENT_LABEL = {
  '-2': 'Hostile',
  '-1': 'Frustrated',
  0: 'Neutral',
  1: 'Positive',
  2: 'Appreciative',
};

/** Prose summary: one cached GLM call, loaded after the panel. */
const SummaryBlock = ({ accessToken, threadId }) => {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    getThreadSummary(accessToken, threadId)
      .then((res) => !cancelled && setState({ loading: false, ...res?.data }))
      .catch(
        () => !cancelled && setState({ loading: false, available: false }),
      );
    return () => {
      cancelled = true;
    };
  }, [accessToken, threadId]);

  return (
    <div className="mb-4 rounded-xl border border-indigo-500/30 bg-indigo-950/30 px-3 py-2.5 shadow-sm">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-300">
        <Sparkles className="h-3.5 w-3.5" /> Summary
      </div>
      {state.loading ? (
        <p className="flex items-center gap-2 text-[13px] text-zinc-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Writing summary…
        </p>
      ) : state.available ? (
        <>
          <p className="text-[13px] leading-relaxed text-zinc-200">
            {state.summary}
          </p>
          {state.next_step && (
            <p className="mt-2 text-[13px] text-zinc-300">
              <span className="font-semibold text-zinc-100">Next: </span>
              {state.next_step}
            </p>
          )}
        </>
      ) : (
        <p className="text-[13px] text-zinc-400">
          Summary not available right now. Everything below comes from stored
          data.
        </p>
      )}
    </div>
  );
};

const ItemRow = ({ item }) => (
  <div className="rounded-xl border border-zinc-700/60 bg-zinc-800/40 p-2.5">
    <div className="flex flex-wrap items-center gap-2">
      <Chip uppercase>{item.kind}</Chip>
      {item.ours && (
        <Chip fg={T.info} bg={T.infoBg}>
          Ours
        </Chip>
      )}
      {item.high_impact && (
        <Chip fg={T.danger} bg={T.dangerBg}>
          High impact
        </Chip>
      )}
      {item.due && (
        <span className="text-[11px] text-zinc-400">
          Due {fmtDate(item.due)}
        </span>
      )}
    </div>
    <p className="mt-1.5 text-[13px] text-zinc-100">{item.text}</p>
    {item.admission === 'PM_CLARIFICATION_REQUIRED' &&
      (item.clarify || []).length > 0 && (
        <p className="mt-0.5 text-[11px] text-amber-300">
          Needs an answer before it counts:{' '}
          {item.clarify
            .map((c) => `${c.field} (${c.role.toUpperCase()})`)
            .join(', ')}
        </p>
      )}
    {item.owner_email && (
      <p className="mt-0.5 text-[11px] text-zinc-400">{item.owner_email}</p>
    )}
    {(item.source_refs || [])
      .filter((r) => r.quote)
      .map((r) => (
        <p
          key={`${r.internet_message_id}-${r.para_id}`}
          className="mt-1.5 border-l-2 border-indigo-500/50 pl-2 text-[12px] italic text-zinc-300"
        >
          &ldquo;{r.quote}&rdquo;
        </p>
      ))}
  </div>
);

const MailRow = ({ mail }) => (
  <li className="flex gap-2.5">
    <span
      className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
      style={{ background: mail.ours ? T.primary : T.muted }}
    />
    <div className="min-w-0">
      <p className="text-[12px] text-zinc-300">
        <span className="font-semibold text-zinc-100">
          {mail.ours ? 'Us' : 'Them'}
        </span>{' '}
        · {fmtDateTime(mail.at)} · {humanise(mail.mail_type)}
        {mail.summary_kind && mail.summary_kind !== 'no_action' && (
          <> · {humanise(mail.summary_kind)}</>
        )}
      </p>
      {mail.objective && (
        <p className="text-[12px] text-zinc-400">{mail.objective}</p>
      )}
    </div>
  </li>
);

/** Thread panel (drawer) for the Mail triage tab. */
const MailThreadPanel = ({ accessToken, thread, onClose }) => {
  if (!thread) return null;
  const tone = labelTone(thread.label);
  const band = BAND_TONE[thread.priority?.band] || BAND_TONE.low;
  const decision = thread.decision;
  const sentiment = thread.sentiment?.latest;

  return (
    <DetailShell
      eyebrow={humanise(thread.thread_type)}
      chips={
        <>
          <Chip fg={tone.fg} bg={tone.bg}>
            {thread.label}
          </Chip>
          <Chip fg={band.fg} bg={band.bg} uppercase>
            {thread.priority?.band}
          </Chip>
          {thread.limited && (
            <Chip fg={T.muted} bg="rgba(161,161,170,0.10)">
              Untracked: limited context
            </Chip>
          )}
        </>
      }
      title={thread.subject || 'Untitled thread'}
      onClose={onClose}
    >
      {(thread.deliverables || []).length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {thread.deliverables.map((d) => (
            <Chip key={d.deliverable_id} fg={T.info} bg={T.infoBg}>
              {d.external_ticket_id || d.deliverable_id}
              {d.jira_status ? ` · ${d.jira_status}` : ''}
              {d.jira_due_date ? ` · due ${fmtDate(d.jira_due_date)}` : ''}
            </Chip>
          ))}
          {thread.tracking && thread.tracking !== 'ON_TRACK' && (
            <Chip fg={T.danger} bg={T.dangerBg}>
              {humanise(thread.tracking.toLowerCase())}
            </Chip>
          )}
        </div>
      )}
      <SummaryBlock accessToken={accessToken} threadId={thread.thread_id} />

      {thread.priority?.reasons?.length > 0 && (
        <div className="mb-4">
          <SectionLabel>Why it is here</SectionLabel>
          <ul className="list-disc pl-5 text-[13px] text-zinc-200">
            {thread.priority.reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {decision && (
        <div className="mb-4">
          <SectionLabel>Decision</SectionLabel>
          <p className="text-[13px] text-zinc-200">
            {humanise(decision.type)} ·{' '}
            {decision.needs_you
              ? 'yours to make'
              : `pending with ${roleLabel(decision.owner_role)}`}
          </p>
          {decision.description && (
            <p className="mt-1 text-[13px] text-zinc-400">
              {decision.description}
            </p>
          )}
        </div>
      )}

      <div className="mb-4 grid grid-cols-2 gap-3 rounded-xl border border-zinc-700/60 bg-zinc-800/40 p-3 sm:grid-cols-4">
        <StatItem
          label="Waiting"
          value={
            thread.waiting_hours != null
              ? `${Math.round(thread.waiting_hours)}h`
              : null
          }
        />
        <StatItem label="Mails" value={thread.rounds} />
        <StatItem
          label="Client tone"
          value={sentiment != null ? SENTIMENT_LABEL[sentiment] : null}
          tone={sentiment != null && sentiment < 0 ? T.danger : undefined}
        />
        <StatItem
          label="Nearest due"
          value={thread.nearest_due ? fmtDate(thread.nearest_due) : null}
          tone={thread.overdue ? T.danger : undefined}
        />
      </div>

      <div className="mb-4">
        <SectionLabel count={thread.role_items?.length || 0}>
          Open items
        </SectionLabel>
        {thread.role_items?.length ? (
          <div className="flex flex-col gap-2">
            {thread.role_items.map((item) => (
              <ItemRow key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <EmptyNote>Nothing open for your role.</EmptyNote>
        )}
      </div>

      <div className="mb-4">
        <SectionLabel>Draft a reply</SectionLabel>
        <DraftPanel
          key={thread.thread_id}
          accessToken={accessToken}
          thread={thread}
        />
      </div>

      <div>
        <SectionLabel count={thread.mails?.length || 0}>Mails</SectionLabel>
        <ul className="flex flex-col gap-2">
          {(thread.mails || []).map((m) => (
            <MailRow key={m.id} mail={m} />
          ))}
        </ul>
      </div>
    </DetailShell>
  );
};

export default MailThreadPanel;
